import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/api';

// ===========================================================================
// Games CSV + Folder Import Dialog
// ===========================================================================
// User picks BOTH a games CSV (with `thumbnail_url` and/or `background_image_url`
// columns referencing filenames) AND a folder of images. We pre-upload only the
// image files whose name appears in either column to the Media Library, build a
// filename→URL map, rewrite both columns, and POST to /admin/games/bulk-import.
// External http(s) URLs and missing-file rows still flow through (the backend's
// `resolve_or_fetch_image_url` handles the rest).
const GameCsvFolderImportDialog = ({ open, onClose, onResult, parseCsv, teams = [], leagues = [] }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [phase, setPhase] = useState('idle'); // idle | review | uploading | importing | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errorMsg, setErrorMsg] = useState('');
  // Review phase: teams the CSV references that don't exist yet.
  // Each entry: { slug, name, league_id }. league_id default = first match
  // for the row's (region, conference) or the first available league.
  const [pendingTeams, setPendingTeams] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);

  const setFolderInput = useCallback((el) => {
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
      el.setAttribute('mozdirectory', '');
    }
  }, []);

  // Reset everything when dialog closes
  useEffect(() => {
    if (!open) {
      setCsvFile(null);
      setImageFiles([]);
      setUpdateExisting(true);
      setPhase('idle');
      setProgress({ done: 0, total: 0 });
      setErrorMsg('');
      setPendingTeams([]);
      setParsedRows([]);
    }
  }, [open]);

  const humanise = (slug) => (slug || '').replace(/_/g, '-').split('-')
    .filter(Boolean).map(p => p[0].toUpperCase() + p.slice(1)).join(' ');

  // Step 1: parse CSV and decide whether we need the review step
  const handleAnalyze = async () => {
    if (!csvFile) { setErrorMsg('Please choose a CSV file.'); return; }
    setErrorMsg('');
    try {
      const text = await csvFile.text();
      const { headers, rows } = parseCsv(text);
      if (rows.length === 0) throw new Error('CSV has no data rows.');
      if (!headers.includes('thumbnail_url') && !headers.includes('background_image_url')) {
        throw new Error('CSV has no thumbnail_url or background_image_url columns. Nothing to match against the image folder.');
      }

      // Collect every team_slug the CSV references
      const knownSlugs = new Set((teams || []).map(t => (t.slug || '').toLowerCase()));
      const missingMap = new Map(); // slug → {slug, name, league_id, sourceRow}
      for (const r of rows) {
        for (const slug of [r.home_team_slug, r.away_team_slug]) {
          const s = (slug || '').toLowerCase().trim();
          if (!s || knownSlugs.has(s) || missingMap.has(s)) continue;
          // Default league: try (region, conference_slug) match → conference_slug → first league
          let leagueId = '';
          const region = (r.region_slug || '').toLowerCase().trim();
          const conf = (r.conference_slug || r.league_slug || '').toLowerCase().trim();
          let match = null;
          if (region && conf) match = leagues.find(lg => (lg.region || '').toLowerCase() === region && (lg.slug || '').toLowerCase() === conf);
          if (!match && conf) match = leagues.find(lg => (lg.slug || '').toLowerCase() === conf);
          if (!match && region) match = leagues.find(lg => (lg.region || '').toLowerCase() === region);
          if (match) leagueId = match.id;
          else if (leagues[0]) leagueId = leagues[0].id;
          missingMap.set(s, { slug: s, name: humanise(s), league_id: leagueId });
        }
      }

      setParsedRows(rows);
      if (missingMap.size > 0) {
        setPendingTeams(Array.from(missingMap.values()));
        setPhase('review');
      } else {
        setPendingTeams([]);
        await runImport(rows, []);
      }
    } catch (err) {
      console.error('CSV parse failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'CSV parse failed');
      setPhase('error');
    }
  };

  const handleReviewConfirm = async () => {
    const blank = pendingTeams.find(t => !t.name || !t.name.trim());
    if (blank) { setErrorMsg(`Please name the team for slug "${blank.slug}".`); return; }
    const noLeague = pendingTeams.find(t => !t.league_id);
    if (noLeague) { setErrorMsg(`Please pick a conference for "${noLeague.slug}".`); return; }
    setErrorMsg('');
    setPhase('uploading'); // borrow phase to show "creating teams…"

    try {
      for (const t of pendingTeams) {
        await api.createTeam({
          league_ids: [t.league_id],
          name: t.name.trim(),
          slug: t.slug,
        });
      }
      await runImport(parsedRows, pendingTeams);
    } catch (err) {
      console.error('Team creation failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'Failed to create team');
      setPhase('error');
    }
  };

  const runImport = async (rows, justCreated) => {
    setPhase('uploading');
    setErrorMsg('');
    try {
      const expected = new Set();
      const isFilename = (v) => v && !/^https?:\/\//i.test(v) && !v.startsWith('/');
      for (const r of rows) {
        if (isFilename(r.thumbnail_url)) expected.add(r.thumbnail_url.toLowerCase());
        if (isFilename(r.background_image_url)) expected.add(r.background_image_url.toLowerCase());
      }
      const filesToUpload = imageFiles.filter(
        f => f.type.startsWith('image/') && expected.has(f.name.toLowerCase())
      );
      const filenameToUrl = {};
      setProgress({ done: 0, total: filesToUpload.length });
      const concurrency = 5;
      let i = 0;
      while (i < filesToUpload.length) {
        const batch = filesToUpload.slice(i, i + concurrency);
        // eslint-disable-next-line no-loop-func
        await Promise.all(batch.map(async (file) => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('alt_text', file.name);
          fd.append('tags', 'game-thumbnail');
          try {
            const res = await api.uploadMedia(fd);
            filenameToUrl[file.name.toLowerCase()] = res.data.url;
          } catch (err) {
            console.error('Image upload failed for', file.name, err);
          } finally {
            setProgress(p => ({ done: p.done + 1, total: p.total }));
          }
        }));
        i += concurrency;
      }
      const rewritten = rows.map(r => {
        const out = { ...r };
        if (r.thumbnail_url) {
          const k = r.thumbnail_url.toLowerCase();
          if (filenameToUrl[k]) out.thumbnail_url = filenameToUrl[k];
        }
        if (r.background_image_url) {
          const k = r.background_image_url.toLowerCase();
          if (filenameToUrl[k]) out.background_image_url = filenameToUrl[k];
        }
        return out;
      });
      setPhase('importing');
      const res = await api.bulkImportGames(rewritten, updateExisting);
      setPhase('done');
      onResult({
        ...res.data,
        _uploadedImages: Object.keys(filenameToUrl).length,
        _createdTeams: (justCreated || []).map(t => t.name),
      });
      onClose();
    } catch (err) {
      console.error('Games CSV+folder import failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'Import failed');
      setPhase('error');
    }
  };

  const handleSubmit = handleAnalyze;

  const imageHint = (() => {
    if (!csvFile || imageFiles.length === 0) return null;
    const imgCount = imageFiles.filter(f => f.type.startsWith('image/')).length;
    return `${imgCount} image file${imgCount === 1 ? '' : 's'} selected. Only files matching CSV "thumbnail_url" or "background_image_url" cells will be uploaded.`;
  })();

  const busy = phase === 'uploading' || phase === 'importing';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl bg-gray-900 border-wpsl-blue/40">
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Bulk Import Games — CSV + Image Folder
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Upload a games CSV alongside a folder of thumbnail / hero images. Images are matched by exact filename in the <code className="text-xs bg-gray-800 px-1 rounded">thumbnail_url</code> or <code className="text-xs bg-gray-800 px-1 rounded">background_image_url</code> columns.
            <br/>
            <span className="text-emerald-300">If the CSV references a team that doesn't exist yet, we'll pause and let you name it + assign a conference</span> before the import runs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {phase === 'review' ? (
            <div className="space-y-3" data-testid="games-dual-import-review">
              {/* Preview tile: how many games we'll create/update + new teams to create */}
              {(() => {
                const seenGames = new Set();
                let willCreate = 0;
                const willUpdate = 0;
                for (const r of parsedRows) {
                  const ht = (r.home_team_slug || '').toLowerCase().trim();
                  const at = (r.away_team_slug || '').toLowerCase().trim();
                  const t = (r.scheduled_time || '').trim();
                  const sig = `${ht}|${at}|${t}`;
                  if (!ht || !at || !t || seenGames.has(sig)) continue;
                  seenGames.add(sig);
                  // We don't have a cheap dedupe against existing games here; count all unique rows as creates.
                  willCreate += 1;
                }
                const dupes = Math.max(0, parsedRows.length - (willCreate + willUpdate));
                return (
                  <div className="grid grid-cols-3 gap-2" data-testid="games-dual-import-preview">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-300" data-testid="games-preview-create-count">{willCreate}</div>
                      <div className="text-xs text-gray-400 uppercase">games to import</div>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-300" data-testid="games-preview-new-teams-count">{pendingTeams.length}</div>
                      <div className="text-xs text-gray-400 uppercase">new teams</div>
                    </div>
                    <div className="bg-gray-700/50 border border-gray-600/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-300" data-testid="games-preview-skip-count">{dupes}</div>
                      <div className="text-xs text-gray-400 uppercase">duplicates</div>
                    </div>
                  </div>
                );
              })()}
              <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 text-sm text-emerald-200">
                Your CSV references <strong>{pendingTeams.length}</strong> team{pendingTeams.length === 1 ? '' : 's'} that don't exist yet.
                Confirm a name and conference for each — these will appear publicly on the site.
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {pendingTeams.map((t, i) => (
                  <div key={t.slug} className="bg-gray-800/60 rounded-lg p-3 border border-gray-700" data-testid={`games-dual-import-pending-${t.slug}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <code className="text-xs bg-gray-900 px-2 py-0.5 rounded text-sky-300">{t.slug}</code>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Team name</label>
                        <input
                          type="text"
                          value={t.name}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPendingTeams(prev => prev.map((p, j) => j === i ? { ...p, name: v } : p));
                          }}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm"
                          data-testid={`games-dual-import-pending-name-${t.slug}`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Conference</label>
                        <select
                          value={t.league_id}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPendingTeams(prev => prev.map((p, j) => j === i ? { ...p, league_id: v } : p));
                          }}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm"
                          data-testid={`games-dual-import-pending-league-${t.slug}`}
                        >
                          <option value="">— choose conference —</option>
                          {leagues.map(lg => (
                            <option key={lg.id} value={lg.id}>{lg.name}{lg.region ? ` (${lg.region})` : ''}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* CSV file picker */}
              <label className="block cursor-pointer">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors ${csvFile ? 'border-wpsl-blue bg-wpsl-blue/10' : 'border-gray-700 hover:border-wpsl-blue/60'}`}
                  data-testid="games-dual-import-csv-dropzone"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Step 1 — CSV</p>
                  <p className="text-white font-medium">
                    {csvFile ? csvFile.name : 'Click to choose games CSV…'}
                  </p>
                  {csvFile && <p className="text-xs text-gray-400">{(csvFile.size / 1024).toFixed(1)} KB</p>}
                </div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setPhase('idle'); setErrorMsg(''); }}
                  disabled={busy}
                  data-testid="games-dual-import-csv-input"
                />
              </label>

              {/* Folder picker */}
              <label className="block cursor-pointer">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors ${imageFiles.length > 0 ? 'border-wpsl-blue bg-wpsl-blue/10' : 'border-gray-700 hover:border-wpsl-blue/60'}`}
                  data-testid="games-dual-import-folder-dropzone"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Step 2 — Image folder</p>
                  <p className="text-white font-medium">
                    {imageFiles.length > 0
                      ? `${imageFiles.length} file${imageFiles.length === 1 ? '' : 's'} selected`
                      : 'Click to choose folder of thumbnail / hero images…'}
                  </p>
                  {imageHint && <p className="text-xs text-gray-400 mt-1">{imageHint}</p>}
                </div>
                <input
                  ref={setFolderInput}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { setImageFiles(Array.from(e.target.files || [])); setPhase('idle'); setErrorMsg(''); }}
                  disabled={busy}
                  data-testid="games-dual-import-folder-input"
                />
              </label>

              {/* Overwrite existing toggle (default ON — typical workflow is "fix thumbnails by re-uploading") */}
              <label className="flex items-start gap-3 bg-gray-800/40 border border-gray-700 rounded-lg p-3 cursor-pointer hover:border-wpsl-blue/50 transition-colors">
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  disabled={busy}
                  className="mt-1 h-4 w-4 accent-wpsl-blue cursor-pointer"
                  data-testid="games-dual-import-update-existing"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">
                    Auto-overwrite existing games &amp; thumbnails (recommended)
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    When ON, re-uploading the same CSV + new image folder will replace the old thumbnail / hero image on every matching game (matched by team + scheduled time). Turn OFF to skip duplicates instead.
                  </p>
                </div>
              </label>
            </>
          )}

          {/* Progress */}
          {phase === 'uploading' && (
            <div className="bg-gray-800/60 rounded-lg p-3 text-sm text-gray-200" data-testid="games-dual-import-progress">
              {progress.total > 0
                ? <>Uploading images: <strong className="text-wpsl-blue">{progress.done}</strong> / {progress.total}</>
                : <>Creating teams…</>}
              <div className="mt-2 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-wpsl-blue transition-all"
                  style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          )}
          {phase === 'importing' && (
            <div className="bg-gray-800/60 rounded-lg p-3 text-sm text-gray-200" data-testid="games-dual-import-progress">
              Importing CSV rows…
            </div>
          )}
          {(phase === 'error' || (phase === 'review' && errorMsg)) && errorMsg && (
            <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-sm text-red-300" data-testid="games-dual-import-error">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            {phase === 'review' && (
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => { setPhase('idle'); setErrorMsg(''); }}
                data-testid="games-dual-import-back"
              >
                ← Back
              </Button>
            )}
            <Button
              variant="outline"
              className="border-gray-600 text-gray-300"
              onClick={() => { if (!busy) onClose(); }}
              disabled={busy}
              data-testid="games-dual-import-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-5"
              onClick={phase === 'review' ? handleReviewConfirm : handleSubmit}
              disabled={busy || (phase !== 'review' && !csvFile)}
              data-testid="games-dual-import-submit"
            >
              {phase === 'uploading' ? (progress.total > 0 ? 'Uploading images…' : 'Creating teams…')
                : phase === 'importing' ? 'Importing…'
                : phase === 'review' ? `Confirm & Import (${pendingTeams.length} new)`
                : 'Upload & Import'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GameCsvFolderImportDialog;
export { GameCsvFolderImportDialog };
